import { Thread_Activity_Type_Enum } from '@gql'

interface RenderedActivity {
  id: string
  type: Thread_Activity_Type_Enum
  createdAt: string
  refMeeting?: { id: string } | null
}

interface CurrentMeeting {
  id: string
  startDate: string
}

// The current meeting's note, temporary ('tmp') or saved, must stay the same
// element of the list: its first save (every 10 s) creates the real activity,
// and a note that changed key or place remounted or moved its editor, which
// lost the cursor (#177). Only the first one counts: two members typing at
// once can save two notes.
function isCurrentMeetingNote(
  activity: RenderedActivity,
  meetingId: string | undefined
) {
  return (
    !!meetingId &&
    activity.type === Thread_Activity_Type_Enum.MeetingNote &&
    activity.refMeeting?.id === meetingId
  )
}

// Chronological order, with the current meeting's note at the meeting's start,
// where its temporary form already sits: the note opens the session, before
// what the session produces (Yohan, 10/08/2026).
export function sortRenderedActivities<T extends RenderedActivity>(
  activities: T[],
  currentMeeting?: CurrentMeeting
): T[] {
  const noteId = activities.find((a) =>
    isCurrentMeetingNote(a, currentMeeting?.id)
  )?.id
  const date = (a: T) =>
    currentMeeting && a.id === noteId ? currentMeeting.startDate : a.createdAt
  return [...activities].sort((a, b) =>
    date(a) < date(b) ? -1 : date(a) > date(b) ? 1 : 0
  )
}

// React keys aligned with `activities`: the current meeting's note keeps
// `meeting_note_<id>` from temporary to saved, the others are keyed by id.
export function getActivityRenderKeys(
  activities: RenderedActivity[],
  currentMeetingId?: string
): string[] {
  let noteKeyTaken = false
  return activities.map((activity) => {
    if (!noteKeyTaken && isCurrentMeetingNote(activity, currentMeetingId)) {
      noteKeyTaken = true
      return `meeting_note_${currentMeetingId}`
    }
    return `activity_${activity.id}`
  })
}
